import { PRO014_STORE_MODE } from "@/@types/campaign";
import type {
  IPro014CampaignDetail,
  IPro014CampaignItem,
  Pro014StepFourItem,
  StoreSelectorValue,
} from "@/@types/campaign";
import type { IGradeTarget } from "@/@types/campaign-grade";

import { ALL_STORES_CODE } from "../components/edit-campaign/constants";
import {
  PRO014_DEFAULT_FIELDS,
  PRO014_MIN_ACTIVE_ITEMS,
  PRO014_RESTRICTION_CHANGE,
  PRO014_WIZARD_TEXTS,
} from "../create/constants";
import type {
  Pro014CampaignData,
  Pro014EditPlan,
  Pro014EditSource,
  Pro014FieldChanges,
  Pro014FieldValues,
  Pro014GradeReplacement,
  Pro014RestrictionChange,
} from "../create/types";
import { toDateInputValue } from "@/utils/date-vigencia";

import { collectGradeTargets, gradesDiffer, toGradeTargetPayloads } from "./grade-targets";

interface OriginalItemState {
  isActive: boolean;
  grades: IGradeTarget[];
}

const FIELD_KEYS: Array<keyof Pro014FieldChanges> = [
  "description",
  "startDate",
  "endDate",
  "takeQuantity",
  "payQuantity",
  "discountValue",
  "discountType",
  "baseValue",
];

export const buildPro014FieldValues = (detail: IPro014CampaignDetail): Pro014FieldValues => ({
  description: detail.description ?? "",
  startDate: toDateInputValue(detail.startDate),
  endDate: toDateInputValue(detail.endDate),
  takeQuantity: detail.takeQuantity ?? PRO014_DEFAULT_FIELDS.TAKE_QUANTITY,
  payQuantity: detail.payQuantity ?? PRO014_DEFAULT_FIELDS.PAY_QUANTITY,
  discountValue: detail.discountValue ?? PRO014_DEFAULT_FIELDS.DISCOUNT_VALUE,
  discountType: detail.discountType ?? PRO014_DEFAULT_FIELDS.DISCOUNT_TYPE,
  paymentMethod: detail.paymentMethod ?? PRO014_DEFAULT_FIELDS.PAYMENT_METHOD,
  baseValue: detail.baseValue ?? PRO014_DEFAULT_FIELDS.BASE_VALUE,
});

export const pro014StoreScope = (
  detail: Pick<IPro014CampaignDetail, "storeMode" | "stores">,
): StoreSelectorValue => ({
  allStores: detail.storeMode === PRO014_STORE_MODE.ALL,
  stores: (detail.stores ?? []).filter((storeCode: number) => storeCode !== ALL_STORES_CODE),
});

const assignField = <K extends keyof Pro014FieldChanges>(
  target: Pro014FieldChanges,
  key: K,
  value: Pro014FieldValues[K],
): void => {
  target[key] = value;
};

export const diffPro014Fields = (
  original: Pro014FieldValues,
  current: Pro014FieldValues,
): Pro014FieldChanges => {
  const changes: Pro014FieldChanges = {};
  FIELD_KEYS.filter((key) => original[key] !== current[key]).forEach((key) =>
    assignField(changes, key, current[key]),
  );
  return changes;
};

const indexOriginalItems = (items: IPro014CampaignItem[]): Map<string, OriginalItemState> =>
  items.reduce((index: Map<string, OriginalItemState>, item: IPro014CampaignItem) => {
    const previous: OriginalItemState | undefined = index.get(item.itemCode);
    const previousGrades: IGradeTarget[] = previous?.grades ?? [];
    index.set(item.itemCode, {
      isActive: Boolean(previous?.isActive) || item.isActive,
      grades: previousGrades.length > 0 ? previousGrades : item.grades ?? [],
    });
    return index;
  }, new Map<string, OriginalItemState>());

const activeWizardItems = (items: IPro014CampaignItem[]): Pro014StepFourItem[] => {
  const names: Map<string, string> = new Map(
    items.map((item: IPro014CampaignItem) => [item.itemCode, item.itemName?.trim() ?? ""]),
  );
  return Array.from(indexOriginalItems(items).entries())
    .filter(([, state]) => state.isActive)
    .map(([itemCode, state]) => ({
      sku: itemCode,
      name: names.get(itemCode) || PRO014_WIZARD_TEXTS.ITEM_FALLBACK_NAME(itemCode),
      grades: state.grades.length > 0 ? state.grades : undefined,
    }));
};

export const buildEmptyPro014Data = (): Pro014CampaignData => ({
  description: "",
  startDate: "",
  endDate: "",
  allStores: true,
  stores: [],
  takeQuantity: PRO014_DEFAULT_FIELDS.TAKE_QUANTITY,
  payQuantity: PRO014_DEFAULT_FIELDS.PAY_QUANTITY,
  discountValue: PRO014_DEFAULT_FIELDS.DISCOUNT_VALUE,
  discountType: PRO014_DEFAULT_FIELDS.DISCOUNT_TYPE,
  paymentMethod: PRO014_DEFAULT_FIELDS.PAYMENT_METHOD,
  baseValue: PRO014_DEFAULT_FIELDS.BASE_VALUE,
  items: [],
  gradeRestricted: false,
});

export const buildPro014EditData = (source: Pro014EditSource): Pro014CampaignData => ({
  ...buildPro014FieldValues(source.detail),
  ...pro014StoreScope(source.detail),
  items: activeWizardItems(source.items),
  gradeRestricted: source.detail.gradeRestricted,
});

export const pro014RestrictionChange = (
  before: boolean,
  after: boolean,
): Pro014RestrictionChange => {
  if (before === after) return PRO014_RESTRICTION_CHANGE.KEEP;
  if (after) return PRO014_RESTRICTION_CHANGE.ENABLE;
  return PRO014_RESTRICTION_CHANGE.DISABLE;
};

const gradeReplacementsOf = (
  current: Pro014CampaignData,
  original: Map<string, OriginalItemState>,
): Pro014GradeReplacement[] => {
  if (!current.gradeRestricted) return [];
  const needsReplacement = (item: Pro014StepFourItem): boolean => {
    const before: OriginalItemState | undefined = original.get(item.sku);
    if (!before) return false;
    return gradesDiffer(before.grades, item.grades);
  };
  return current.items.filter(needsReplacement).map((item: Pro014StepFourItem) => ({
    itemCode: item.sku,
    targets: toGradeTargetPayloads(item.sku, item.grades),
  }));
};

export const buildPro014EditPlan = (
  source: Pro014EditSource,
  current: Pro014CampaignData,
): Pro014EditPlan => {
  const original: Map<string, OriginalItemState> = indexOriginalItems(source.items);
  const restriction: Pro014RestrictionChange = pro014RestrictionChange(
    source.detail.gradeRestricted,
    current.gradeRestricted,
  );
  const currentSkus: Set<string> = new Set(current.items.map((item) => item.sku));
  const newItems: Pro014StepFourItem[] = current.items.filter((item) => !original.has(item.sku));
  const activeItemsCount: number = Array.from(original.values()).filter(
    (state: OriginalItemState) => state.isActive,
  ).length;

  return {
    fields: diffPro014Fields(buildPro014FieldValues(source.detail), current),
    restriction,
    activeItemsCount,
    itemsToAdd: newItems.map((item: Pro014StepFourItem) => item.sku),
    addGradeTargets: current.gradeRestricted ? collectGradeTargets(newItems) : undefined,
    itemsToReactivate: current.items
      .filter((item: Pro014StepFourItem) => original.get(item.sku)?.isActive === false)
      .map((item: Pro014StepFourItem) => item.sku),
    itemsToDeactivate: Array.from(original.entries())
      .filter(([itemCode, state]) => state.isActive && !currentSkus.has(itemCode))
      .map(([itemCode]) => itemCode),
    gradeReplacements: gradeReplacementsOf(current, original),
  };
};

export const leavesTooFewActiveItems = (
  source: Pro014EditSource | undefined,
  items: Pro014StepFourItem[],
): boolean => {
  if (!source) return false;
  const currentSkus: Set<string> = new Set(items.map((item: Pro014StepFourItem) => item.sku));
  const deactivates: boolean = source.items.some(
    (item: IPro014CampaignItem) => item.isActive && !currentSkus.has(item.itemCode),
  );
  return deactivates && items.length < PRO014_MIN_ACTIVE_ITEMS;
};

export const hasPro014EditChanges = (plan: Pro014EditPlan): boolean =>
  Object.keys(plan.fields).length > 0 ||
  plan.restriction !== PRO014_RESTRICTION_CHANGE.KEEP ||
  plan.itemsToAdd.length > 0 ||
  plan.itemsToReactivate.length > 0 ||
  plan.itemsToDeactivate.length > 0 ||
  plan.gradeReplacements.length > 0;
